import { Component, computed, inject, input, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractManageComponent } from '@unity/abstract/abstract-manage.component';
import {
  TransitionRequestDto,
  UnityWorkflowState,
  UploadsClient,
  VendorDetailedDto,
  VendorsClient,
  VendorTypesClient,
  VendorUpdateDto,
} from '@unity/clients';
import { AuthorizeDirective } from '@unity/directives/authorize.directive';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { FileUploaderControlComponent } from '@unity/forms/file-uploader-control/file-uploader-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { FormLayoutComponent } from '@unity/layouts/form-layout/form-layout.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { DownloadService } from '@unity/services/download.service';
import { ActionDeleteSharedComponent } from '@unity/shared/composed/action-delete-shared/action-delete-shared.component';
import { ActionDownloadSharedComponent } from '@unity/shared/composed/action-download-shared/action-download-shared.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { PanelManageSharedComponent } from '@unity/shared/composed/panel-manage-shared/panel-manage-shared.component';
import { BannerAlertSharedComponent } from '@unity/shared/presentation/banner-alert-shared/banner-alert-shared.component';
import { WorkflowSelectorSharedComponent } from '@unity/shared/workflow-selector-shared/workflow-selector-shared.component';
import { numericFormControl, stringFormControl } from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityEntitlementPath } from '@unity/utils/types';
import { take, tap } from 'rxjs';

class VendorUpdateSchema implements FormGroupControlSchema<VendorUpdateDto> {
  public name: FormControl<string>;
  public type_id: FormControl<number>;
  public tin: FormControl<string>;
  public w9_upload_id: FormControl<number>;

  public constructor(vendor?: VendorDetailedDto) {
    this.name = stringFormControl({ disabled: true, value: vendor?.name });
    this.type_id = numericFormControl({ disabled: true, value: vendor?.type.id });
    this.tin = stringFormControl({ disabled: true, value: vendor?.tin });
    this.w9_upload_id = numericFormControl({ disabled: true, value: vendor?.w9_upload?.id });
  }
}

@Component({
  selector: 'unity-manage-vendor-page',
  standalone: true,
  imports: [
    KENDO_STACKLAYOUT,
    ReactiveFormsModule,
    PageContainerComponent,
    TextboxControlComponent,
    FormLayoutComponent,
    BannerAlertSharedComponent,
    PanelManageSharedComponent,
    ActionDeleteSharedComponent,
    AuthorizeDirective,
    WorkflowSelectorSharedComponent,
    DropdownControlComponent,
    ActionDownloadSharedComponent,
    FileUploaderControlComponent,
    ActionSharedComponent,
  ],
  templateUrl: './manage-vendor-page.component.html',
  styleUrl: './manage-vendor-page.component.scss',
})
export class ManageVendorPageComponent extends AbstractManageComponent<VendorDetailedDto, VendorUpdateDto, VendorUpdateSchema> {
  private readonly vendorsClient = inject(VendorsClient);
  private readonly vendorTypesClient = inject(VendorTypesClient);
  private readonly uploadsClient = inject(UploadsClient);

  private readonly downloadService = inject(DownloadService);

  private readonly vendorTypesResource = rxResource({
    stream: () => this.vendorTypesClient.getAllAsDetailed(),
  });

  public readonly $vendorId = input.required({ alias: 'vendorId', transform: (value: string) => parseInt(value) });

  protected override mainResource = rxResource({
    params: () => {
      const resourceId = this.$resourceId();
      return resourceId ? { resourceId } : undefined;
    },
    stream: ({ params: { resourceId } }) => this.vendorsClient.getOneAsDetailed(resourceId),
  });

  protected override updateResourceFn$ = (resourceId: number, form: FormGroup<VendorUpdateSchema>) =>
    this.vendorsClient.updateOne(resourceId, form.getRawValue());

  protected override deleteResourceFn$ = (resourceId: number) => this.vendorsClient.deleteOne(resourceId);

  protected override availableTransitionsFn$ = (resourceId: number) => this.availableTransitionsClient.getAllForVendorAsMini(resourceId);

  protected override doTransitionFn$ = (resourceId: number, transition: TransitionRequestDto) =>
    this.transitionsClient.setForVendor(resourceId, transition);

  protected override newControlsFn(dto?: VendorDetailedDto) {
    return new VendorUpdateSchema(dto);
  }

  protected override editEntitlement: UnityEntitlementPath = 'Vendor:Update';
  protected override transitionEntitlement: UnityEntitlementPath = 'Vendor:Transition';
  protected override deleteEntitlement: UnityEntitlementPath = 'Vendor:Delete';
  protected override resourceName = 'Vendor';
  protected override $resourceId = this.$vendorId;
  protected override readonly $contentHeader = computed(() => `Vendor: ${this.$resource()?.name}`);

  protected readonly $isDisabled = computed(() => this.$resource()?.current_state.enum_value == UnityWorkflowState.Disabled);
  protected readonly $isDownloading = signal(false);

  protected readonly $vendorTypes = computed(() => {
    if (!this.vendorTypesResource.hasValue()) {
      return [];
    }
    return this.vendorTypesResource.value().data;
  });

  protected downloadW9File() {
    const vendor = untracked(this.$resource);
    if (!vendor || !vendor.w9_upload) {
      return;
    }

    this.$isDownloading.set(true);
    this.uploadsClient
      .downloadOne(vendor.w9_upload.id)
      .pipe(
        take(1),
        tap(file => this.downloadService.saveFile(file))
      )
      .subscribe({
        complete: () => this.$isDownloading.set(false),
      });
  }
  protected returnToDashboardAction = QuickActions.ReturnToDashboard;
}
